import { useState, useEffect } from "react";
import type { ChangesSinceTimestampOutput, ChangeSummary } from "@hantavirus/shared";
import type { TranslateFn } from "#/lib/i18n.js";

export interface WhatsNewBannerProps {
  changes: ChangesSinceTimestampOutput;
  locale: string;
  t: TranslateFn;
}

const SEVERITY_VAR: Record<string, string> = {
  info: "var(--color-accent-info, #3b82f6)",
  warning: "var(--color-accent-warning, #f59e0b)",
  emergency: "var(--color-accent-emergency, #dc2626)",
};

function SeverityDot({ severity }: { severity: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: "0.5rem",
        height: "0.5rem",
        borderRadius: "50%",
        backgroundColor: SEVERITY_VAR[severity] ?? SEVERITY_VAR.info,
        flexShrink: 0,
      }}
    />
  );
}

function formatRelativeTime(iso: string, locale: string): string {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60_000);
    const hours = Math.floor(diff / 3_600_000);
    const days = Math.floor(diff / 86_400_000);
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    if (days > 0) return rtf.format(-days, "day");
    if (hours > 0) return rtf.format(-hours, "hour");
    return rtf.format(-minutes, "minute");
  } catch {
    return iso;
  }
}

function pluralTitle(count: number, locale: string, t: TranslateFn): string {
  if (locale === "ar") {
    if (count === 0) return t("whatsNew.banner.title_zero", { count });
    if (count === 1) return t("whatsNew.banner.title_one", { count });
    if (count === 2) return t("whatsNew.banner.title_two", { count });
    if (count >= 3 && count <= 10) return t("whatsNew.banner.title_few", { count });
    if (count >= 11 && count <= 99) return t("whatsNew.banner.title_many", { count });
    return t("whatsNew.banner.title_other", { count });
  }
  if (locale === "fr") {
    return count <= 1
      ? t("whatsNew.banner.title", { count })
      : t("whatsNew.banner.title_other", { count });
  }
  // English
  return count === 1
    ? t("whatsNew.banner.title", { count })
    : t("whatsNew.banner.title_other", { count });
}

function ChangeItem({
  item,
  locale,
  t,
}: {
  item: ChangeSummary;
  locale: string;
  t: TranslateFn;
}) {
  return (
    <li
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "0.625rem",
        paddingBlock: "0.625rem",
        borderBottom: "1px solid var(--color-border, #e5e7eb)",
        fontSize: "0.875rem",
      }}
    >
      <SeverityDot severity={item.severity} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <span style={{ color: "var(--color-foreground, #111827)" }}>
          {item.summary}
        </span>
        <span
          style={{
            display: "inline-block",
            marginInlineStart: "0.5rem",
            color: "var(--color-muted-foreground, #6b7280)",
            fontSize: "0.75rem",
          }}
        >
          {formatRelativeTime(item.occurredAt, locale)}
        </span>
      </div>
      {item.sourceUrl && (
        <a
          href={item.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "var(--color-primary, #2563eb)",
            fontSize: "0.75rem",
            whiteSpace: "nowrap",
            minHeight: "44px",
            display: "inline-flex",
            alignItems: "center",
            paddingInline: "0.5rem",
            textDecoration: "underline",
          }}
        >
          {t("whatsNew.banner.viewSource")}
        </a>
      )}
    </li>
  );
}

export function WhatsNewBanner({ changes, locale, t }: WhatsNewBannerProps) {
  const [visible, setVisible] = useState(changes.count > 0);
  const [expanded, setExpanded] = useState(false);

  // Update hv_last_seen cookie on mount so the next visit shows only newer changes.
  useEffect(() => {
    if (changes.count > 0) {
      const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
      document.cookie = `hv_last_seen=${encodeURIComponent(new Date().toISOString())}; path=/; SameSite=Lax; max-age=${90 * 24 * 60 * 60}; expires=${expires}`;
    }
  }, [changes.count]);

  if (!visible || changes.count === 0) return null;

  function handleMarkAsSeen() {
    const expires = new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toUTCString();
    document.cookie = `hv_last_seen=${encodeURIComponent(new Date().toISOString())}; path=/; SameSite=Lax; max-age=${90 * 24 * 60 * 60}; expires=${expires}`;
    setVisible(false);
  }

  const titleText = pluralTitle(changes.count, locale, t);

  return (
    <div
      role="region"
      aria-label={titleText}
      style={{
        backgroundColor: "var(--color-card, #ffffff)",
        borderBottom: "1px solid var(--color-border, #e5e7eb)",
      }}
    >
      {/* Header row */}
      <div
        style={{
          maxWidth: "76rem",
          marginInline: "auto",
          paddingInline: "1rem",
          paddingBlock: "0.75rem",
          display: "flex",
          alignItems: "center",
          gap: "0.75rem",
          flexWrap: "wrap",
        }}
      >
        {/* Count badge */}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "1.5rem",
            height: "1.5rem",
            paddingInline: "0.375rem",
            borderRadius: "9999px",
            backgroundColor: "var(--color-accent-emergency, #dc2626)",
            color: "#ffffff",
            fontWeight: 700,
            fontSize: "0.75rem",
            fontFamily: "var(--font-mono)",
          }}
        >
          {changes.count}
        </span>

        {/* Title */}
        <span
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "var(--color-foreground, #111827)",
            flex: 1,
          }}
        >
          {titleText}
        </span>

        {/* Expand/collapse toggle */}
        <button
          type="button"
          onClick={() => setExpanded((e) => !e)}
          aria-expanded={expanded}
          style={{
            minHeight: "44px",
            minWidth: "44px",
            paddingInline: "0.75rem",
            paddingBlock: "0.5rem",
            borderRadius: "0.375rem",
            border: "1px solid var(--color-border, #e5e7eb)",
            backgroundColor: "transparent",
            cursor: "pointer",
            fontSize: "0.8125rem",
            color: "var(--color-foreground, #111827)",
          }}
        >
          {expanded ? t("whatsNew.banner.collapse") : t("whatsNew.banner.expand")}
        </button>

        {/* Mark as seen */}
        <button
          type="button"
          onClick={handleMarkAsSeen}
          style={{
            minHeight: "44px",
            minWidth: "44px",
            paddingInline: "0.75rem",
            paddingBlock: "0.5rem",
            borderRadius: "0.375rem",
            border: "none",
            backgroundColor: "var(--color-muted, #f3f4f6)",
            cursor: "pointer",
            fontSize: "0.8125rem",
            color: "var(--color-muted-foreground, #6b7280)",
          }}
        >
          {t("whatsNew.banner.markAsSeen")}
        </button>
      </div>

      {/* Expanded list */}
      {expanded && (
        <div
          style={{
            maxWidth: "76rem",
            marginInline: "auto",
            paddingInline: "1rem",
            paddingBottom: "0.75rem",
          }}
        >
          <ul
            aria-label={t("whatsNew.banner.expand")}
            style={{ listStyle: "none", margin: 0, padding: 0 }}
          >
            {changes.items.map((item) => (
              <ChangeItem key={item.id} item={item} locale={locale} t={t} />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
